import { Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import type { FileRelatedType } from "@bookdental/schemas";
import { FilesGrid } from "./FilesGrid";
import { FilesTable } from "./FilesTable";
import type { FileRow } from "./files-types";

const NO_TAG_LABEL = "Sem tag";
const noop = () => {};

export function EntityFilesView({
  relatedType,
  relatedId,
  viewMode,
  onOpenFile,
  onDownloadFile,
  onTagsFile,
  onShareFile,
  onHistoryFile,
  onRenameFile,
  onMoveFile,
  onDeleteFile,
}: {
  relatedType: FileRelatedType;
  relatedId: string;
  viewMode: "grade" | "lista";
  onOpenFile: (file: FileRow) => void;
  onDownloadFile: (file: FileRow) => void;
  onTagsFile: (file: FileRow) => void;
  onShareFile: (file: FileRow) => void;
  onHistoryFile: (file: FileRow) => void;
  onRenameFile: (file: FileRow) => void;
  onMoveFile: (file: FileRow) => void;
  onDeleteFile: (file: FileRow) => void;
}) {
  const filesQuery = trpc.file.listByEntity.useQuery({ relatedType, relatedId });

  if (filesQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }

  const files = (filesQuery.data ?? []) as FileRow[];

  if (files.length === 0) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white py-16 text-center dark:border-border dark:bg-card">
        <p className="text-sm font-medium text-zinc-900 dark:text-foreground">Nenhum arquivo relacionado ainda</p>
      </div>
    );
  }

  // Agrupa por tag — um arquivo com várias tags aparece em vários grupos (esperado: é a mesma
  // organização por assunto que uma pasta automática representaria, só que computada).
  const groups = new Map<string, FileRow[]>();
  for (const file of files) {
    const tags = file.tags?.map((t) => t.tag.name) ?? [];
    const labels = tags.length > 0 ? tags : [NO_TAG_LABEL];
    for (const label of labels) {
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label)!.push(file);
    }
  }

  const actions = {
    onOpenFolder: noop,
    onRenameFolder: noop,
    onMoveFolder: noop,
    onDeleteFolder: noop,
    onOpenFile,
    onDownloadFile,
    onTagsFile,
    onShareFile,
    onHistoryFile,
    onRenameFile,
    onMoveFile,
    onDeleteFile,
  };

  return (
    <div className="space-y-6">
      {[...groups.entries()].map(([label, groupFiles]) => (
        <div key={label}>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-400 dark:text-muted-foreground">
            {label}
          </h3>
          {viewMode === "grade" ? (
            <FilesGrid folders={[]} files={groupFiles} actions={actions} />
          ) : (
            <FilesTable folders={[]} files={groupFiles} actions={actions} />
          )}
        </div>
      ))}
    </div>
  );
}
